/**
 * Shared tenancy wire types (PRO-0030).
 *
 * Single source of truth for the `GET /schools` response shape, consumed by BOTH
 * tiers: the API (`apps/api`, building the response) and the SPA (`apps/web`,
 * rendering the active-school switcher and the L6 cross-school view). The SPA
 * imports wire **types** as `import type` from `@ucp/web/lib`, so nothing in the
 * shared db/domain lib reaches the browser bundle. The SPA must import runtime
 * values (e.g. `UCP_SCHOOL_ID_HEADER`) from this module directly — not via the
 * barrel — or Vite will pull in Node-only dependencies such as `pg`.
 */

/** HTTP header the SPA sends to bind tenant-scoped API calls (matches `resolveTenant`). */
export const UCP_SCHOOL_ID_HEADER = 'x-ucp-school-id';

export interface SchoolSummary {
  readonly schoolId: string;
  readonly name: string;
}

export interface SchoolsResponse {
  /** The bound single-school context; null for an L6 cross-school read. */
  readonly activeSchoolId: string | null;
  readonly mode: 'single' | 'cross-school-read';
  /** One item for L1/L2; all active schools for an L6 cross-school read. */
  readonly schools: readonly SchoolSummary[];
}

/**
 * A school in the RIP placement-school picker (ADR-017 addendum). Extends the
 * summary with the establishment's curated HOME `localAuthorityId` (nullable —
 * NULL = unknown, shown only in the full list, never shortlisted) so the client can
 * float schools matching the referral's identified LA to the top.
 *
 * PRO-1010 / ADR-052 D2: `latitude`/`longitude` are the geocoded (non-PII)
 * establishment coordinates used to render the school as a map pin. Nullable — a
 * not-yet-geocoded school has NULL coordinates (no pin), but stays fully selectable
 * from the accessible drop-down. Additive fields; the shape is backward-compatible.
 */
export interface ReferralPlacementSchool extends SchoolSummary {
  readonly localAuthorityId: string | null;
  readonly latitude: number | null;
  readonly longitude: number | null;
}

export interface ReferralPlacementSchoolsResponse {
  readonly schools: readonly ReferralPlacementSchool[];
}
